// @ts-ignore
// @ts-nocheck

/**
 * pwtracker-required-attrs.js
 * ----------------------------------------------------------------------------
 * Builds and maintains the "required attributes" bundle attached to every PW
 * tracking event: device/OS/browser/network info, UTM params, env, plus the
 * anonymous_id (long-lived, localStorage) and session_id (30 min, cookie)
 * identifiers used to stitch events together.
 *
 * Exposed globally as window.PWTrackerRequiredAttrs.
 *
 * Function flow
 * ----------------------------------------------------------------------------
 *   getOrCreateRequiredAttributes()  [public: getOrCreate]
 *     |
 *     |-- cache hit? -----------------> mergeFreshUtmValues() -> attachTrackingIds()
 *     |
 *     |-- else read localStorage ----> parse + migrate legacy nulls/ids
 *     |                                 -> mergeFreshUtmValues() -> attachTrackingIds()
 *     |
 *     '-- else first run -------------> buildRequiredAttributes()
 *                                         |-- detectOperatingSystem()
 *                                         |-- detectOSVersion()
 *                                         |-- detectDeviceModel()
 *                                         |-- detectBrowserName()
 *                                         |-- detectIsInWebview() -> detectHasPWAppBridge()
 *                                         |-- getEnvValue()
 *                                         '-- getUTMValuesFromExistingSource()
 *                                       -> persist to localStorage
 *                                       -> mergeFreshUtmValues() -> attachTrackingIds()
 *
 *   attachTrackingIds() -> attachAnonymousId() -> getAnonymousId()
 *     Reads/creates the persistent anonymous_id (localStorage), restoring one
 *     preserved in sessionStorage across a logout-triggered storage clear.
 *
 *   Session (cookie-based, separate from anonymous_id):
 *     getCurrentSessionId()            [public: getSessionId]
 *       -> read-only; null if missing/expired, does not create or extend.
 *     touchSessionForInternalEvent()   [public: touchSessionForInternalEvent]
 *       -> called only when an internal SDK event fires; creates a new
 *          session via createSessionState() if missing/expired, otherwise
 *          returns the existing session_id unchanged (no expiry extension).
 *     createSessionState() -> generateSessionId() + persistSessionState()
 *       -> persistSessionState() writes SESSION_ID_COOKIE / SESSION_CREATED_AT_COOKIE
 *          via setSessionCookie().
 *
 *   Logout / storage-clear flow:
 *     clearLocalStoragePreservingAnonymousId()  [public]
 *       -> preserveAnonymousIdOnLogout() (anon id -> sessionStorage)
 *       -> localStorage.clear()
 *       -> restoreAnonymousIdAfterLogoutClear() (sessionStorage -> localStorage)
 *
 *   getShopifySessionId()  [public]
 *     -> reads Shopify's own `_shopify_s` cookie (independent of the above).
 */
(function () {
  var REQUIRED_ATTRS_STORAGE_KEY = "pwtracker_required_attrs_v1";
  var ANON_ID_STORAGE_KEY = "pwtracker_anonymous_id_v1";
  var ANON_ID_PRESERVE_SESSION_KEY = "pwtracker_anonymous_id_preserve_v1";
  var SESSION_ID_COOKIE = "pwtracker_session_id_v1";
  var SESSION_CREATED_AT_COOKIE = "pwtracker_session_created_at_v1";
  var SESSION_LIFETIME_SEC = 30 * 60;
  var SESSION_LIFETIME_MS = SESSION_LIFETIME_SEC * 1000;
  var requiredAttrsCache = null;

  function safeLocalStorageGet(key) {
    try {
      return window.localStorage ? window.localStorage.getItem(key) : null;
    } catch (e) {
      return null;
    }
  }

  function safeLocalStorageSet(key, value) {
    try {
      if (window.localStorage) {
        window.localStorage.setItem(key, value);
      }
    } catch (e) {}
  }

  function safeSessionStorageGet(key) {
    try {
      return window.sessionStorage ? window.sessionStorage.getItem(key) : null;
    } catch (e) {
      return null;
    }
  }

  function safeSessionStorageSet(key, value) {
    try {
      if (window.sessionStorage) {
        window.sessionStorage.setItem(key, value);
      }
    } catch (e) {}
  }

  function safeSessionStorageRemove(key) {
    try {
      if (window.sessionStorage) {
        window.sessionStorage.removeItem(key);
      }
    } catch (e) {}
  }

  function getAnonymousId() {
    var existingId = safeLocalStorageGet(ANON_ID_STORAGE_KEY);
    if (existingId) return existingId;

    var preservedId = safeSessionStorageGet(ANON_ID_PRESERVE_SESSION_KEY);
    if (preservedId) {
      safeLocalStorageSet(ANON_ID_STORAGE_KEY, preservedId);
      safeSessionStorageRemove(ANON_ID_PRESERVE_SESSION_KEY);
      return preservedId;
    }

    var newId = null;
    try {
      if (window.crypto && typeof window.crypto.randomUUID === "function") {
        newId = "anon_" + window.crypto.randomUUID();
      }
    } catch (e) {}
    if (!newId) {
      newId = "anon_" + String(Date.now()) + "_" + String(Math.floor(Math.random() * 1000000));
    }
    safeLocalStorageSet(ANON_ID_STORAGE_KEY, newId);
    return newId;
  }

  /** Browser-lifetime ID in localStorage; no expiry; survives logout until user clears site data. */
  function attachAnonymousId(attrs) {
    if (!attrs || typeof attrs !== "object") return attrs;
    attrs.anonymous_id = getAnonymousId();
    return attrs;
  }

  function attachTrackingIds(attrs) {
    attachAnonymousId(attrs);
    try {
      attrs.user_agent = navigator.userAgent || "";
      attrs.referrer = document.referrer || "";
    } catch (e) {
      attrs.user_agent = attrs.user_agent || "";
      attrs.referrer = attrs.referrer || "";
    }
    if (typeof window.getFromAppFlag === "function") {
      attrs.is_in_webview = window.getFromAppFlag();
    }
    return attrs;
  }

  function setSessionCookie(name, value) {
    try {
      if (typeof document === "undefined") return;
      var secure = location && location.protocol === "https:" ? "; Secure" : "";
      document.cookie =
        encodeURIComponent(name) +
        "=" +
        encodeURIComponent(String(value)) +
        "; Max-Age=" +
        String(SESSION_LIFETIME_SEC) +
        "; Path=/" +
        "; SameSite=Lax" +
        secure;
    } catch (e) {}
  }

  function getSessionCookie(name) {
    try {
      if (typeof document === "undefined" || !document.cookie) return null;
      var prefix = encodeURIComponent(name) + "=";
      var parts = document.cookie.split("; ");
      for (var i = 0; i < parts.length; i++) {
        if (parts[i].indexOf(prefix) === 0) {
          return decodeURIComponent(parts[i].substring(prefix.length));
        }
      }
    } catch (e) {}
    return null;
  }

  /** Shopify analytics session cookie (`_shopify_s`), used as shopify_session_id in PW events. */
  function getShopifySessionId() {
    return getSessionCookie("_shopify_s");
  }

  function generateSessionId() {
    try {
      if (window.crypto && typeof window.crypto.randomUUID === "function") {
        return "sess_" + window.crypto.randomUUID();
      }
    } catch (e) {}
    return "sess_" + String(Date.now()) + "_" + String(Math.floor(Math.random() * 1000000));
  }

  function parseSessionCreatedAtMs(rawValue) {
    if (rawValue == null || rawValue === "") return null;
    var parsed = parseInt(String(rawValue), 10);
    return isNaN(parsed) || parsed <= 0 ? null : parsed;
  }

  function isSessionExpired(createdAtMs, nowMs) {
    if (createdAtMs == null) return true;
    return nowMs - createdAtMs >= SESSION_LIFETIME_MS;
  }

  function persistSessionState(sessionId, createdAtMs) {
    setSessionCookie(SESSION_ID_COOKIE, sessionId);
    setSessionCookie(SESSION_CREATED_AT_COOKIE, String(createdAtMs));
  }

  function createSessionState() {
    var sessionId = generateSessionId();
    var createdAtMs = Date.now();
    persistSessionState(sessionId, createdAtMs);
    return sessionId;
  }

  /** Read current session_id if still within its 30 min lifetime (does not create or extend). */
  function getCurrentSessionId() {
    var sessionId = getSessionCookie(SESSION_ID_COOKIE);
    var createdAtMs = parseSessionCreatedAtMs(getSessionCookie(SESSION_CREATED_AT_COOKIE));
    if (!sessionId || isSessionExpired(createdAtMs, Date.now())) return null;
    return sessionId;
  }

  /**
   * Called only when an internal SDK event fires.
   * session_id is valid for 30 min from creation; expiry is not extended by later events.
   */
  function touchSessionForInternalEvent() {
    var now = Date.now();
    var sessionId = getSessionCookie(SESSION_ID_COOKIE);
    var createdAtMs = parseSessionCreatedAtMs(getSessionCookie(SESSION_CREATED_AT_COOKIE));

    if (!sessionId || isSessionExpired(createdAtMs, now)) {
      return createSessionState();
    }

    return sessionId;
  }

  function detectHasPWAppBridge() {
    try {
      // React Native WebView
      if (window.ReactNativeWebView && typeof window.ReactNativeWebView.postMessage === "function") {
        return true;
      }

      // Android native bridge (PW app)
      if (window.JSBridge && typeof window.JSBridge.postMessage === "function") {
        return true;
      }

      // iOS WKWebView message handler (PW app)
      if (
        window.webkit &&
        window.webkit.messageHandlers &&
        window.webkit.messageHandlers.appBridge &&
        typeof window.webkit.messageHandlers.appBridge.postMessage === "function"
      ) {
        return true;
      }
    } catch (e) {}

    return false;
  }

  function detectIsInWebview(userAgent) {
    if (typeof window.getFromAppFlag === "function") {
      return window.getFromAppFlag();
    }

    if (window.__PW_APP_WEBVIEW__ === true || window.__PW_APP__ === true) return true;
    if (detectHasPWAppBridge()) return true;
    if (!userAgent) return false;
    const ua = String(userAgent).toLowerCase();
    return /pwapp|physicswallah|physicswallahapp/.test(ua);
  }

  function detectOperatingSystem(userAgent) {
    if (!userAgent) return null;
    if (/android/i.test(userAgent)) return "Android";
    if (/iPhone|iPad|iPod/i.test(userAgent)) return "iOS";
    if (/Windows/i.test(userAgent)) return "Windows";
    if (/Macintosh|Mac OS X/i.test(userAgent)) return "macOS";
    if (/Linux/i.test(userAgent)) return "Linux";
    return null;
  }

  function detectOSVersion(userAgent, operatingSystem) {
    if (!userAgent || !operatingSystem) return null;
    var match = null;
    if (operatingSystem === "Android") {
      match = userAgent.match(/Android\s([0-9\.]+)/i);
      return match ? match[1] : null;
    }
    if (operatingSystem === "iOS") {
      match = userAgent.match(/OS\s([0-9_]+)/i);
      return match ? match[1].replace(/_/g, ".") : null;
    }
    if (operatingSystem === "Windows") {
      match = userAgent.match(/Windows NT\s([0-9\.]+)/i);
      return match ? match[1] : null;
    }
    if (operatingSystem === "macOS") {
      match = userAgent.match(/Mac OS X\s([0-9_]+)/i);
      return match ? match[1].replace(/_/g, ".") : null;
    }
    return null;
  }

  function detectBrowserName() {
    if (navigator.userAgentData && Array.isArray(navigator.userAgentData.brands)) {
      var brands = navigator.userAgentData.brands;
      for (var i = 0; i < brands.length; i++) {
        var brand = brands[i] && brands[i].brand ? brands[i].brand : "";
        if (brand && brand !== "Not A(Brand)") {
          return brand;
        }
      }
    }

    var ua = navigator.userAgent || "";
    if (/Edg\//.test(ua)) return "Edge";
    if (/Chrome\//.test(ua) && !/Edg\//.test(ua)) return "Chrome";
    if (/Firefox\//.test(ua)) return "Firefox";
    if (/Safari\//.test(ua) && !/Chrome\//.test(ua)) return "Safari";
    if (/OPR\//.test(ua) || /Opera\//.test(ua)) return "Opera";
    return null;
  }

  function detectDeviceModel(userAgent, operatingSystem) {
    if (!userAgent) return null;
    if (/iPhone/i.test(userAgent)) return "iPhone";
    if (/iPad/i.test(userAgent)) return "iPad";
    if (/iPod/i.test(userAgent)) return "iPod";
    if (operatingSystem === "Android") {
      var match = userAgent.match(/Android\s[0-9\.]+;\s*([^;\)]+)\s*(?:Build|\))/i);
      if (match && match[1]) return match[1].trim();
      return "Android Device";
    }
    // Desktop browsers do not expose a hardware model in the UA; use an OS-level label.
    if (operatingSystem === "Windows") return "Windows PC";
    if (operatingSystem === "macOS") return "Mac";
    if (operatingSystem === "Linux") return "Linux PC";
    return null;
  }

  function getEnvValue() {
    var gtmConfig = window.__GTM_CONFIG__ || {};
    if (gtmConfig.env) return gtmConfig.env;
    var host = (location && location.hostname ? String(location.hostname) : "").toLowerCase();
    if (host === "shop.pw.live") return "prod";
    if (host === "shopify-marketplace-stage.physicswallah.live") return "staging";
    if (host && /dev|staging|localhost/i.test(host)) return "staging";
    return "prod";
  }

  function getUTMValuesFromExistingSource() {
    var urlParams = null;
    try {
      if (typeof URLSearchParams !== "undefined") {
        urlParams = new URLSearchParams(window.location.search || "");
      }
    } catch (e) {
      urlParams = null;
    }
    var fromSource = {};
    if (window.pwURLAttributes && typeof window.pwURLAttributes.getAll === "function") {
      fromSource = window.pwURLAttributes.getAll() || {};
    } else if (window.pwURLAttributes && typeof window.pwURLAttributes.getJson === "function") {
      fromSource = window.pwURLAttributes.getJson() || {};
    }

    function pickValue(key) {
      var v = fromSource[key] || (urlParams ? urlParams.get(key) : null);
      // Use `undefined` for "missing" so JSON.stringify omits the key (avoids persisted nulls).
      if (v === null || v === undefined || v === "") return undefined;
      return v;
    }

    return {
      utm_source: pickValue("utm_source"),
      utm_medium: pickValue("utm_medium"),
      utm_campaign: pickValue("utm_campaign"),
      utm_content: pickValue("utm_content"),
      utm_term: pickValue("utm_term"),
    };
  }
  
  function buildRequiredAttributes() {
    var userAgent = navigator.userAgent || "";
    var operatingSystem = detectOperatingSystem(userAgent);
    var connection = navigator.connection || navigator.mozConnection || navigator.webkitConnection;
    var utmValues = getUTMValuesFromExistingSource();

    return {
      is_in_webview: detectIsInWebview(userAgent),
      env: getEnvValue(),
      os_version: detectOSVersion(userAgent, operatingSystem),
      device_model: detectDeviceModel(userAgent, operatingSystem),
      operating_system: operatingSystem,
      browser: detectBrowserName(),
      network_type: connection && connection.effectiveType ? connection.effectiveType : null,
      rtt_ms: connection && typeof connection.rtt === "number" ? connection.rtt : null,
      utm_source: utmValues.utm_source,
      utm_medium: utmValues.utm_medium,
      utm_campaign: utmValues.utm_campaign,
      utm_content: utmValues.utm_content,
      utm_term: utmValues.utm_term,
      language: navigator.language || null,
    };
  }

  var UTM_ATTR_KEYS = ["utm_source", "utm_medium", "utm_campaign", "utm_content", "utm_term"];

  function mergeFreshUtmValues(attrs) {
    if (!attrs || typeof attrs !== "object") return attrs;
    var utmValues = getUTMValuesFromExistingSource();
    var didMutate = false;
    for (var i = 0; i < UTM_ATTR_KEYS.length; i++) {
      var k = UTM_ATTR_KEYS[i];
      var v = utmValues[k];
      if (v === undefined || v === null || v === "") continue;
      if (attrs[k] !== v) {
        attrs[k] = v;
        didMutate = true;
      }
    }
    if (didMutate) {
      safeLocalStorageSet(REQUIRED_ATTRS_STORAGE_KEY, JSON.stringify(attrs));
    }
    return attrs;
  }

  function getOrCreateRequiredAttributes() {
    if (requiredAttrsCache) {
      mergeFreshUtmValues(requiredAttrsCache);
      return attachTrackingIds(requiredAttrsCache);
    }

    var storedRaw = safeLocalStorageGet(REQUIRED_ATTRS_STORAGE_KEY);
    if (storedRaw) {
      try {
        requiredAttrsCache = JSON.parse(storedRaw);
        if (requiredAttrsCache && typeof requiredAttrsCache === "object") {
          // Migration: older versions stored missing UTMs as explicit `null`.
          // Clean them so downstream systems don't see persisted nulls.
          var utmKeys = UTM_ATTR_KEYS;
          var didMutate = false;
          for (var i = 0; i < utmKeys.length; i++) {
            var k = utmKeys[i];
            if (requiredAttrsCache[k] === null) {
              try {
                delete requiredAttrsCache[k];
                didMutate = true;
              } catch (e) {}
            }
          }
          if (requiredAttrsCache.session_id != null) {
            try {
              delete requiredAttrsCache.session_id;
              didMutate = true;
            } catch (e) {}
          }
          if (requiredAttrsCache.anonymous_id != null) {
            try {
              delete requiredAttrsCache.anonymous_id;
              didMutate = true;
            } catch (e) {}
          }
          if (didMutate) {
            safeLocalStorageSet(REQUIRED_ATTRS_STORAGE_KEY, JSON.stringify(requiredAttrsCache));
          }
          mergeFreshUtmValues(requiredAttrsCache);
          return attachTrackingIds(requiredAttrsCache);
        }
      } catch (e) {}
    }

    requiredAttrsCache = buildRequiredAttributes();
    mergeFreshUtmValues(requiredAttrsCache);
    safeLocalStorageSet(REQUIRED_ATTRS_STORAGE_KEY, JSON.stringify(requiredAttrsCache));
    return attachTrackingIds(requiredAttrsCache);
  }

  function preserveAnonymousIdOnLogout() {
    var anonymousId = safeLocalStorageGet(ANON_ID_STORAGE_KEY) || getAnonymousId();
    if (anonymousId) {
      safeSessionStorageSet(ANON_ID_PRESERVE_SESSION_KEY, anonymousId);
    }
    return { anonymousId: anonymousId || null };
  }

  function restoreAnonymousIdAfterLogoutClear(preserved) {
    var anonymousId =
      (preserved && preserved.anonymousId) || safeSessionStorageGet(ANON_ID_PRESERVE_SESSION_KEY);
    if (anonymousId) {
      safeLocalStorageSet(ANON_ID_STORAGE_KEY, anonymousId);
      safeSessionStorageRemove(ANON_ID_PRESERVE_SESSION_KEY);
    }
  }

  function clearLocalStoragePreservingAnonymousId() {
    var preserved = preserveAnonymousIdOnLogout();
    try {
      if (window.localStorage) {
        window.localStorage.clear();
      }
    } catch (e) {}
    restoreAnonymousIdAfterLogoutClear(preserved);
  }

  function generateEventId() {
    try {
      if (window.crypto && typeof window.crypto.randomUUID === "function") {
        return window.crypto.randomUUID();
      }
    } catch (e) {}
    return String(Date.now()) + "_" + String(Math.floor(Math.random() * 1000000));
  }

  /** Same anonymous_id / session_id / event_id used for GA and internal SDK payloads. */
  function getTrackingFieldsForEvent() {
    const attrs = getOrCreateRequiredAttributes();
    return {
      anonymous_id: attrs.anonymous_id || getAnonymousId(),
      session_id: touchSessionForInternalEvent(),
      event_id: generateEventId(),
    };
  }

  window.PWTrackerRequiredAttrs = {
    getOrCreate: getOrCreateRequiredAttributes,
    getAnonymousId: getAnonymousId,
    getSessionId: getCurrentSessionId,
    getShopifySessionId: getShopifySessionId,
    touchSessionForInternalEvent: touchSessionForInternalEvent,
    generateEventId: generateEventId,
    getTrackingFieldsForEvent: getTrackingFieldsForEvent,
    clearLocalStoragePreservingAnonymousId: clearLocalStoragePreservingAnonymousId,
    ready: function () {
      return Promise.resolve(null);
    },
  };
})();
